/**
 * Shared layout tokens for wide onboarding screens (Figma 91-28466 / 91-32601).
 * Login/DBT-verification/OTP/recover-DBT card is 460px (Figma 91-27123);
 * registration + recover use 1296px content width (72px page gutters,
 * 24px card padding, 16px field grid gap, ~300px columns).
 */
export const LOGIN_CARD_MAX_WIDTH = (theme) => theme.palette.layout.onboardingCardMaxWidth;
/** External DBT registration portal — Figma 91-28680 "Register Now" tertiary CTA */
export const DBT_REGISTRATION_PORTAL_URL = "https://dbtagriculture.bihar.gov.in/";

/**
 * OTP resend-cooldown / expiry window, shared by every onboarding OTP screen
 * (login, reset-password, recover-DBT-id) and FarmerDbtVerification's own
 * AGENT lookup — one place to change if the value needs to move.
 */
export const OTP_RESEND_SECONDS = 10 * 60;

/** Common gap between the login/OTP/recover card and the page footer. */
export const onboardingPageBottomPaddingSx = { pb: "52px" };

// NOT theme.palette.layout.pagePadding (72px @ md) — this wrap is always
// paired with `maxWidth: LOGIN_CARD_MAX_WIDTH` (460px) by its callers, and
// (Tailwind preflight sets box-sizing: border-box app-wide) padding on a
// maxWidth-capped box is subtracted FROM that budget, not added outside it —
// 72px each side would shrink the 460px card's actual content width down to
// 316px. The page-gutter token belongs on genuinely full-bleed elements only
// (the breadcrumb bar, and onboardingPageWrapWideSx below, both maxWidth:none)
// where padding creates the intended edge inset instead of eating the card.
// This is a mobile-only safety gutter so the 100%-wide-but-460px-capped box
// doesn't touch the viewport edge below the breakpoint where mx:auto centering
// starts leaving room on its own.
export const onboardingPageWrapSx = {
  width: "100%",
  mx: "auto",
  px: { xs: 1.5, sm: 0 },
};

/** Figma 91-28680 — 72px page gutters on wide registration/recover screens */
export const onboardingPageWrapWideSx = {
  width: "100%",
  mx: "auto",
  px: (theme) => theme.palette.layout.pagePadding,
};

export const onboardingCardSx = {
  backgroundColor: "background.paper",
  borderRadius: "16px",
  border: 1,
  borderColor: "divider",
  boxShadow: (theme) => theme.palette.layout.cardShadow,
  px: (theme) => theme.palette.layout.onboardingCardPaddingX,
  py: (theme) => theme.palette.layout.cardPadding,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

export const onboardingFieldGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(4, minmax(0, 1fr))",
  },
  gap: 2,
};

export const onboardingTwoColGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    md: "repeat(2, minmax(0, 1fr))",
  },
  gap: 2,
};

/** Figma 91-28702 — gender / Jeevika pill groups at fixed 300px width */
export const onboardingChoiceRowSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: 2,
};

export const onboardingChoiceColSx = {
  width: { xs: "100%", md: "300px" },
};

/** Figma Important Link Item — #FAEBC7 / #F0BE49 */
export const onboardingInfoBannerSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  p: 1.5,
  borderRadius: "12px",
  bgcolor: "accent.butterscotchBg",
  border: 1,
  borderColor: "accent.butterscotch",
};

export const onboardingInputSx = {
  "& .MuiOutlinedInput-root fieldset": { borderRadius: "8px" },
  "& .MuiInputBase-input::placeholder": {
    color: "neutral.placeholder",
    opacity: 1,
  },
};

export const onboardingSubmitRowSx = {
  display: "flex",
  justifyContent: { xs: "stretch", lg: "flex-end" },
  pt: 2,
};

export const onboardingSubmitButtonSx = {
  width: { xs: "100%", lg: "300px" },
  minHeight: 48,
};
